import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@material-ui/core/styles";
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Pagination from '../../components/Pagination';
import { getUsers, getUserHistory, getUserAnalyticsBreakdown, deleteUser } from '../../services/userService';
import ConfirmDialog from '../../components/ConfirmDialog';
import DirectNotificationDialog from './DirectNotificationDialog';
import DirectInAppMessageDialog from './DirectInAppMessageDialog';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Drawer, Box, Typography, List, ListItem, ListItemText, makeStyles,
  CircularProgress, Snackbar, Tooltip, Avatar, Chip, TextField, InputAdornment, Button,
  FormControl, Select, MenuItem, Grid, Card, CardContent, Divider
} from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import DateRangeIcon from '@material-ui/icons/DateRange';
import CloseIcon from '@material-ui/icons/Close';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  statCard: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#10b981',
      transform: 'translateY(-2px)',
    },
  },
  statValue: {
    fontWeight: 800,
    fontSize: '1.4rem',
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
  actionBtnNotif: {
    color: '#10b981',
    padding: 7,
    background: 'rgba(16,185,129,0.1)',
    marginRight: 6,
    '&:hover': {
      backgroundColor: 'rgba(16,185,129,0.2)',
    },
  },
  actionBtnInApp: {
    color: '#0ea5e9',
    padding: 7,
    background: 'rgba(14,165,233,0.1)',
    marginRight: 6,
    '&:hover': {
      backgroundColor: 'rgba(14,165,233,0.2)',
    },
  },
  actionBtnHistory: {
    color: '#8b5cf6',
    padding: 7,
    background: 'rgba(139,92,246,0.1)',
    marginRight: 6,
    '&:hover': {
      backgroundColor: 'rgba(139,92,246,0.2)',
    },
  },
  actionBtnDelete: {
    color: '#ef4444',
    padding: 7,
    background: 'rgba(239,68,68,0.1)',
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.2)',
    },
  },
  historyDrawer: {
    width: 460,
    maxWidth: '90vw',
    background: theme.palette.background.paper,
    height: '100%',
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    background: theme.palette.type === "dark" ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.6)",
    borderBottom: '1px solid rgba(148,163,184,0.08)',
  },
  drawerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
  },
  userCard: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    background: theme.palette.type === 'dark' ? 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(30,41,59,0) 100%)' : 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(255,255,255,0) 100%)',
    borderBottom: '1px solid rgba(148,163,184,0.08)',
  },
  avatar: {
    width: 56,
    height: 56,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
  },
  userInfo: {
    flex: 1,
  },
  metaCard: {
    margin: theme.spacing(2, 3),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.7)' : '#f8fafc',
    border: `1px solid ${theme.palette.divider}`,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    '&:not(:last-child)': {
      borderBottom: `1px dashed ${theme.palette.divider}`,
    },
  },
  metaLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  metaValue: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  historyList: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  historyItem: {
    background: theme.palette.type === "dark" ? "rgba(30,41,59,0.6)" : "rgba(255,255,255,0.6)",
    borderRadius: 12,
    marginBottom: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  historyText: {
    '& .MuiListItemText-primary': {
      color: theme.palette.text.primary,
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    '& .MuiListItemText-secondary': {
      color: theme.palette.text.secondary,
      fontSize: '0.8rem',
      marginTop: 4,
    },
  },
  versionBadge: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.75rem',
    height: 24,
  },
  searchField: {
    minWidth: 260,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    },
  },
  filterSelect: {
    minWidth: 170,
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
  },
  deviceChip: {
    fontSize: '0.7rem',
    height: 20,
    fontWeight: 600,
  },
}));

export default function UserListPage() {
  const classes = useStyles();
  const theme = useTheme();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialActivityParam = searchParams.get('activity') || searchParams.get('filter');
  const defaultActivity = (initialActivityParam === 'today' || initialActivityParam === 'daily') ? 'daily' : 'all';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [versionFilter, setVersionFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState(defaultActivity);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Analytics breakdown
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    topVersions: [],
    notTrackedCount: 0,
    platforms: { android: 0, ios: 0, web: 0 },
    growth: { activeToday: 0, activeLast7Days: 0 },
  });

  // Modals & Drawers state
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifTargetUser, setNotifTargetUser] = useState(null);
  const [inAppTargetUser, setInAppTargetUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const loadData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetPage = opts.page !== undefined ? opts.page : 1;
    const targetSearch = opts.search !== undefined ? opts.search : search;
    const targetVersion = opts.version !== undefined ? opts.version : versionFilter;
    const targetActivity = opts.activity !== undefined ? opts.activity : activityFilter;
    const targetStartDate = opts.startDate !== undefined ? opts.startDate : customStartDate;
    const targetEndDate = opts.endDate !== undefined ? opts.endDate : customEndDate;

    try {
      const [result, breakdown] = await Promise.all([
        getUsers({
          page: targetPage,
          search: targetSearch,
          versionFilter: targetVersion,
          activityFilter: targetActivity,
          startDate: targetStartDate,
          endDate: targetEndDate,
        }),
        getUserAnalyticsBreakdown(),
      ]);
      setItems(result.items);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      setPage(result.page);
      setAnalytics(breakdown);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load users' });
    }
    setLoading(false);
  }, [search, versionFilter, activityFilter, customStartDate, customEndDate]);

  useEffect(() => {
    loadData({ page: 1, activity: defaultActivity });
  }, [loadData, defaultActivity]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleShowHistory = async (user) => {
    setSelectedUser(user);
    setHistoryLoading(true);
    try {
      const userHistory = await getUserHistory(user.id);
      setHistory(userHistory);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load user history' });
    }
    setHistoryLoading(false);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteUser(userToDelete.id);
      setSnackbar({
        open: true,
        message: `User ${userToDelete.displayName || userToDelete.name || userToDelete.email || userToDelete.id} successfully deleted`,
      });
      if (selectedUser && selectedUser.id === userToDelete.id) {
        setSelectedUser(null);
      }
      setUserToDelete(null);
      loadData({ page });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete user' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminLayout title="Users Directory & App Versions" onRefresh={() => loadData({ page: 1 })}>
      {/* Top Metric Cards */}
      <Box mb={3}>
        <Grid container spacing={2}>
          {/* Card 1: Daily Active Users (Opened Today) */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              className={classes.statCard}
              elevation={0}
              style={{
                cursor: 'pointer',
                borderColor: activityFilter === 'daily' ? '#10b981' : undefined,
                background: activityFilter === 'daily' ? 'rgba(16,185,129,0.08)' : undefined,
              }}
              onClick={() => {
                const next = activityFilter === 'daily' ? 'all' : 'daily';
                setActivityFilter(next);
                loadData({ page: 1, activity: next });
              }}
            >
              <CardContent style={{ padding: 16 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography className={classes.statLabel}>Opened Today (Daily)</Typography>
                  <Chip
                    size="small"
                    label={activityFilter === 'daily' ? 'Active Filter' : 'Click to View'}
                    style={{
                      height: 18,
                      fontSize: '0.65rem',
                      background: activityFilter === 'daily' ? '#10b981' : 'rgba(16,185,129,0.15)',
                      color: activityFilter === 'daily' ? '#fff' : '#10b981',
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Typography className={classes.statValue} style={{ color: '#10b981' }}>
                  {analytics.growth?.activeToday || 0}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.72rem' }}>
                  Users who opened the app today
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 2: 7-Day Active Users */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              className={classes.statCard}
              elevation={0}
              style={{
                cursor: 'pointer',
                borderColor: activityFilter === 'last7Days' ? '#3b82f6' : undefined,
                background: activityFilter === 'last7Days' ? 'rgba(59,130,246,0.08)' : undefined,
              }}
              onClick={() => {
                const next = activityFilter === 'last7Days' ? 'all' : 'last7Days';
                setActivityFilter(next);
                loadData({ page: 1, activity: next });
              }}
            >
              <CardContent style={{ padding: 16 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography className={classes.statLabel}>Weekly Active (7D)</Typography>
                  <Chip
                    size="small"
                    label={activityFilter === 'last7Days' ? 'Active Filter' : 'Click to View'}
                    style={{
                      height: 18,
                      fontSize: '0.65rem',
                      background: activityFilter === 'last7Days' ? '#3b82f6' : 'rgba(59,130,246,0.15)',
                      color: activityFilter === 'last7Days' ? '#fff' : '#3b82f6',
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Typography className={classes.statValue} style={{ color: '#3b82f6' }}>
                  {analytics.growth?.activeLast7Days || 0}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.72rem' }}>
                  Active in past 7 days
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 3: Total App Users */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard} elevation={0}>
              <CardContent style={{ padding: 16 }}>
                <Typography className={classes.statLabel}>Total App Users</Typography>
                <Typography className={classes.statValue}>
                  {analytics.totalUsers || totalCount}
                </Typography>
                <Box display="flex" style={{ gap: 8 }} mt={0.5}>
                  <Chip
                    label={`🤖 Android: ${analytics.platforms?.android || 0}`}
                    size="small"
                    style={{ fontSize: '0.68rem', height: 20 }}
                  />
                  <Chip
                    label={`🍏 iOS: ${analytics.platforms?.ios || 0}`}
                    size="small"
                    style={{ fontSize: '0.68rem', height: 20 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 4: Versions */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard} elevation={0}>
              <CardContent style={{ padding: 16 }}>
                <Typography className={classes.statLabel}>App Versions</Typography>
                <Box mt={0.8} display="flex" flexWrap="wrap" style={{ gap: 6, maxHeight: 46, overflowY: 'auto' }}>
                  {analytics.topVersions && analytics.topVersions.length > 0 ? (
                    analytics.topVersions.slice(0, 3).map((v, i) => (
                      <Chip
                        key={i}
                        label={`${v.version}: ${v.count}`}
                        size="small"
                        className={classes.versionBadge}
                        style={{ fontSize: '0.7rem', height: 20 }}
                      />
                    ))
                  ) : null}
                  {analytics.notTrackedCount > 0 && (
                    <Chip
                      label={`Legacy: ${analytics.notTrackedCount}`}
                      size="small"
                      style={{
                        fontSize: '0.7rem',
                        height: 20,
                        background: 'rgba(148,163,184,0.12)',
                        color: '#94a3b8',
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Action Bar / Filters */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" style={{ gap: 16 }}>
        <div>
          <Typography variant="body2" color="textSecondary">
            Monitor registered users, daily app opens, device platforms, and send direct push or in-app messages.
          </Typography>
        </div>

        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
          {/* App Open / Activity Filter Dropdown */}
          <FormControl size="small" variant="outlined" className={classes.filterSelect}>
            <Select
              value={activityFilter}
              onChange={(e) => {
                setActivityFilter(e.target.value);
                loadData({ page: 1, activity: e.target.value });
              }}
              displayEmpty
            >
              <MenuItem value="all">👥 All Activity</MenuItem>
              <MenuItem value="daily">🟢 Opened Today (Daily)</MenuItem>
              <MenuItem value="yesterday">🕒 Active Yesterday</MenuItem>
              <MenuItem value="last7Days">📅 Active in Last 7 Days</MenuItem>
              <MenuItem value="last30Days">🗓️ Active in Last 30 Days</MenuItem>
              <MenuItem value="custom">📆 Custom Activity Range...</MenuItem>
            </Select>
          </FormControl>

          {/* Custom Date Range Picker */}
          {activityFilter === 'custom' && (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <DateRangeIcon fontSize="small" style={{ color: '#10b981' }} />
              <TextField
                type="date"
                size="small"
                variant="outlined"
                label="From"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 140 }}
              />
              <TextField
                type="date"
                size="small"
                variant="outlined"
                label="To"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 140 }}
              />
              <Button
                size="small"
                variant="contained"
                style={{ background: '#10b981', color: '#fff', textTransform: 'none', height: 40 }}
                onClick={() => loadData({ page: 1, startDate: customStartDate, endDate: customEndDate })}
                disabled={!customStartDate || !customEndDate}
              >
                Apply
              </Button>
            </Box>
          )}

          {/* Version Filter */}
          <FormControl size="small" variant="outlined" className={classes.filterSelect}>
            <Select
              value={versionFilter}
              onChange={(e) => {
                setVersionFilter(e.target.value);
                loadData({ page: 1, version: e.target.value });
              }}
              displayEmpty
            >
              <MenuItem value="all">📱 All App Versions</MenuItem>
              {analytics.topVersions?.map((v, i) => (
                <MenuItem key={i} value={v.version.replace(/^v/, '')}>
                  {v.version} ({v.count})
                </MenuItem>
              ))}
              {analytics.notTrackedCount > 0 && (
                <MenuItem value="none">Not Updated Yet ({analytics.notTrackedCount})</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Search Field */}
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by name, email, version..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {(activityFilter !== 'all' || versionFilter !== 'all' || search) && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setActivityFilter('all');
                setVersionFilter('all');
                setSearch('');
                setCustomStartDate('');
                setCustomEndDate('');
                loadData({ page: 1, activity: 'all', version: 'all', search: '', startDate: null, endDate: null });
              }}
              style={{ color: '#ef4444', textTransform: 'none' }}
            >
              Reset
            </Button>
          )}

          <Chip
            label={`Users: ${totalCount}`}
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
              color: '#10b981',
              fontWeight: 700,
              height: 38,
              borderRadius: 10,
            }}
          />
        </Box>
      </Box>

      {/* Main Users Table */}
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} thickness={4} />
          <Typography className={classes.loadingText}>Loading user records...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="30%">User Profile</TableCell>
                <TableCell width="22%">App Version & Platform</TableCell>
                <TableCell width="16%">Registration Date</TableCell>
                <TableCell width="18%">App Open / Last Active</TableCell>
                <TableCell width="14%" align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ padding: 40, color: '#94a3b8' }}>
                    No users matching "{search}"
                  </TableCell>
                </TableRow>
              ) : (
                items.map((user) => {
                  const hasFcm = Boolean(user.fcmToken);
                  const hasVersion = Boolean(user.appVersion || user.appVersionFull);
                  const displayVer = user.appVersion ? `v${user.appVersion}` : user.appVersionFull;

                  return (
                    <TableRow key={user.id} hover>
                      {/* User Profile */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar 
                            src={user.photoUrl || user.photoURL}
                            style={{ width: 42, height: 42, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}
                          >
                            <PersonOutlineIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {user.displayName || user.name || 'Unknown User'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.78rem' }}>
                              {user.email || 'No email'}
                            </Typography>
                            <Typography variant="caption" style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                              UID: {user.id ? `${user.id.substring(0, 10)}...` : '-'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* App Version & Platform */}
                      <TableCell>
                        {hasVersion ? (
                          <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <Chip
                                label={displayVer}
                                size="small"
                                className={classes.versionBadge}
                              />
                              {user.buildNumber && (
                                <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.72rem' }}>
                                  Build {user.buildNumber}
                                </Typography>
                              )}
                            </Box>

                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <Chip
                                label={hasFcm ? 'Push Active' : 'No FCM'}
                                size="small"
                                className={classes.deviceChip}
                                style={{
                                  background: hasFcm ? 'rgba(16,185,129,0.12)' : 'rgba(148,163,184,0.15)',
                                  color: hasFcm ? '#10b981' : '#94a3b8',
                                }}
                              />
                              {user.platform && (
                                <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.72rem', textTransform: 'capitalize', fontWeight: 600 }}>
                                  • {user.platform}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.82rem' }}>
                              —
                            </Typography>
                            {hasFcm && (
                              <Chip
                                label="Push Active"
                                size="small"
                                className={classes.deviceChip}
                                style={{
                                  background: 'rgba(16,185,129,0.12)',
                                  color: '#10b981',
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </TableCell>

                      {/* Registration Date */}
                      <TableCell>
                        <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
                          {formatDate(user.createdAt || user.lastActive || user.lastOpened)}
                        </Typography>
                      </TableCell>

                      {/* App Open / Last Active */}
                      <TableCell>
                        {(() => {
                          const actVal = user.lastActive || user.lastOpened;
                          if (!actVal) {
                            return (
                              <Typography variant="caption" color="textSecondary">
                                No activity recorded
                              </Typography>
                            );
                          }
                          const aDate = actVal.toDate ? actVal.toDate() : new Date(actVal);
                          if (isNaN(aDate.getTime())) {
                            return <Typography variant="caption" color="textSecondary">—</Typography>;
                          }

                          const now = new Date();
                          const sToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
                          const sYesterday = new Date(sToday.getTime() - 86400000);

                          if (aDate >= sToday) {
                            return (
                              <Chip
                                size="small"
                                label={`Opened Today ${aDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                                style={{
                                  background: 'rgba(16,185,129,0.15)',
                                  color: '#10b981',
                                  fontWeight: 700,
                                  fontSize: '0.72rem',
                                  height: 24,
                                }}
                              />
                            );
                          } else if (aDate >= sYesterday && aDate < sToday) {
                            return (
                              <Chip
                                size="small"
                                label={`Yesterday ${aDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                                style={{
                                  background: 'rgba(59,130,246,0.12)',
                                  color: '#3b82f6',
                                  fontWeight: 600,
                                  fontSize: '0.72rem',
                                  height: 24,
                                }}
                              />
                            );
                          } else {
                            return (
                              <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
                                {formatDate(actVal)}
                              </Typography>
                            );
                          }
                        })()}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8 }}>
                          {/* 1. Send Direct Notification */}
                          <Tooltip title="Send Direct Push Notification">
                            <IconButton
                              size="small"
                              className={classes.actionBtnNotif}
                              onClick={() => setNotifTargetUser(user)}
                            >
                              <NotificationsActiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* 2. Send Direct In-App Message */}
                          <Tooltip title="Send In-App Modal / Popup">
                            <IconButton
                              size="small"
                              className={classes.actionBtnInApp}
                              onClick={() => setInAppTargetUser(user)}
                            >
                              <ChatBubbleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* 3. View History */}
                          <Tooltip title="View User & Activity History">
                            <IconButton
                              size="small"
                              className={classes.actionBtnHistory}
                              onClick={() => handleShowHistory(user)}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* 4. Delete User */}
                          <Tooltip title="Delete User Information">
                            <IconButton
                              size="small"
                              className={classes.actionBtnDelete}
                              onClick={() => setUserToDelete(user)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && totalCount > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* User Activity & Device Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
      >
        <div className={classes.historyDrawer}>
          <div className={classes.drawerHeader}>
            <Typography className={classes.drawerTitle}>User Profile & Device Info</Typography>
            <IconButton onClick={() => setSelectedUser(null)} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          {selectedUser && (
            <>
              {/* Header profile */}
              <div className={classes.userCard}>
                <Avatar className={classes.avatar} src={selectedUser.photoUrl || selectedUser.photoURL}>
                  <PersonOutlineIcon />
                </Avatar>
                <div className={classes.userInfo}>
                  <Typography variant="h6" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {selectedUser.displayName || selectedUser.name || 'Unknown User'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                    {selectedUser.email || 'No email associated'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                    UID: {selectedUser.id}
                  </Typography>

                  <Box mt={1.5} display="flex" style={{ gap: 8 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<NotificationsActiveIcon />}
                      style={{ fontSize: '0.75rem', borderColor: '#10b981', color: '#10b981' }}
                      onClick={() => {
                        const u = selectedUser;
                        setSelectedUser(null);
                        setNotifTargetUser(u);
                      }}
                    >
                      Notify
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ChatBubbleOutlineIcon />}
                      style={{ fontSize: '0.75rem', borderColor: '#0ea5e9', color: '#0ea5e9' }}
                      onClick={() => {
                        const u = selectedUser;
                        setSelectedUser(null);
                        setInAppTargetUser(u);
                      }}
                    >
                      In-App Msg
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DeleteOutlineIcon />}
                      style={{ fontSize: '0.75rem', borderColor: '#ef4444', color: '#ef4444' }}
                      onClick={() => setUserToDelete(selectedUser)}
                    >
                      Delete
                    </Button>
                  </Box>
                </div>
              </div>

              {/* App Version & Device Card */}
              <div className={classes.metaCard}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, color: '#10b981' }}>
                  📱 App Version & Device Info
                </Typography>

                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>
                    <SystemUpdateAltIcon fontSize="inherit" style={{ color: '#10b981' }} /> App Version:
                  </span>
                  <span className={classes.metaValue}>
                    {selectedUser.appVersionFull || (selectedUser.appVersion ? `v${selectedUser.appVersion} (${selectedUser.buildNumber || '1'})` : '—')}
                  </span>
                </div>

                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>
                    <PhoneAndroidIcon fontSize="inherit" style={{ color: '#f59e0b' }} /> Platform / OS:
                  </span>
                  <span className={classes.metaValue} style={{ textTransform: 'capitalize' }}>
                    {selectedUser.platform || '—'}
                  </span>
                </div>

                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>
                    <NotificationsActiveIcon fontSize="inherit" style={{ color: selectedUser.fcmToken ? '#10b981' : '#94a3b8' }} /> Push Token:
                  </span>
                  <span className={classes.metaValue}>
                    {selectedUser.fcmToken ? 'Active & Registered' : 'Not Available'}
                  </span>
                </div>

                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>
                    <AccessTimeIcon fontSize="inherit" style={{ color: '#10b981' }} /> Last App Open:
                  </span>
                  <span className={classes.metaValue}>
                    {formatDate(selectedUser.lastActive || selectedUser.lastOpened)}
                  </span>
                </div>

                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>
                    <AccessTimeIcon fontSize="inherit" style={{ color: '#64748b' }} /> Member Since:
                  </span>
                  <span className={classes.metaValue}>
                    {formatDate(selectedUser.createdAt || selectedUser.lastActive || selectedUser.lastOpened)}
                  </span>
                </div>
              </div>
            </>
          )}

          <Divider style={{ margin: '8px 24px' }} />
          <Box px={3} pt={1}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, color: theme.palette.text.secondary }}>
              Recent Activity Logs
            </Typography>
          </Box>

          <div className={classes.historyList}>
            {historyLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={32} />
              </Box>
            ) : history.length === 0 ? (
              <Box textAlign="center" p={4} color="textSecondary">
                <Typography variant="body2">No recent activity found for this user.</Typography>
              </Box>
            ) : (
              <List>
                {history.map((h, i) => (
                  <ListItem key={i} className={classes.historyItem}>
                    <ListItemText
                      className={classes.historyText}
                      primary={h.title || h.action || 'Viewed item'}
                      secondary={`${h.category || 'General'} • ${formatDate(h.viewedAt)}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </div>
        </div>
      </Drawer>

      {/* Direct Push Notification Dialog */}
      <DirectNotificationDialog
        open={Boolean(notifTargetUser)}
        user={notifTargetUser}
        onClose={() => setNotifTargetUser(null)}
        onSuccess={(msg) => setSnackbar({ open: true, message: msg })}
      />

      {/* Direct In-App Popup Dialog */}
      <DirectInAppMessageDialog
        open={Boolean(inAppTargetUser)}
        user={inAppTargetUser}
        onClose={() => setInAppTargetUser(null)}
        onSuccess={(msg) => setSnackbar({ open: true, message: msg })}
      />

      {/* Delete User Confirmation Dialog */}
      <ConfirmDialog
        open={Boolean(userToDelete)}
        title="Delete User"
        message={
          userToDelete
            ? `Are you sure you want to permanently delete this user "${userToDelete.displayName || userToDelete.name || userToDelete.email || userToDelete.id}"? All associated data and activity history will be removed.`
            : ''
        }
        confirmText={deleteLoading ? 'Deleting...' : 'Delete User'}
        confirmColor="error"
        onConfirm={handleDeleteUser}
        onCancel={() => {
          if (!deleteLoading) setUserToDelete(null);
        }}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </AdminLayout>
  );
}